import { createClient } from 'graphql-ws';
import { createYoga } from 'graphql-yoga';
import { dashboardMockData, agentsMockData, knowledgeGraphMockData, skillsMockData, customerSegmentsMockData, customersMockData } from './mock-data';
import { policiesMockData } from './mock-data/policy-skills-mock';

// Always use mock data for the demo
const useMockData = () => {
  return true;
};

const GRAPHQL_ENDPOINT = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000/graphql'
  : process.env.VITE_API_URL + '/graphql';

const wsClient = createClient({
  url: GRAPHQL_ENDPOINT.replace('http', 'ws')
});

export const graphqlClient = createYoga({
  endpoint: GRAPHQL_ENDPOINT,
  fetchOptions: {
    credentials: 'include'
  },
  wsClient,
  onError: (error) => {
    console.error('GraphQL Error:', error);
  }
});

// Simple function to handle mock responses
function handleMockResponse(query, variables) {
  console.log('=== Using Mock Data ===');
  
  // Check for dashboard query
  if (query.includes('GetDashboardMetrics')) {
    console.log('Returning mock dashboard data');
    return { dashboardData: dashboardMockData };
  }
  
  // Check for agents query
  if (query.includes('GetAgents')) {
    console.log('Returning mock agents data');
    
    // Apply filter if present
    if (variables && variables.filter) {
      const filter = variables.filter;
      let filteredItems = [...agentsMockData.items];
      
      if (filter.status) {
        filteredItems = filteredItems.filter(agent => agent.status === filter.status);
      }
      if (filter.channel) {
        filteredItems = filteredItems.filter(agent => agent.channel === filter.channel);
      }
      
      return { 
        agentsList: {
          ...agentsMockData,
          items: filteredItems,
          totalCount: filteredItems.length
        } 
      };
    }
    
    return { agentsList: agentsMockData };
  }
  
  // Check for knowledge graph query
  if (query.includes('GetKnowledgeGraph')) {
    console.log('Returning mock knowledge graph data');
    return { knowledgeGraph: knowledgeGraphMockData };
  }
  
  // Check for skills query
  if (query.includes('GetSkills')) {
    console.log('Returning mock skills data');
    return { skills: skillsMockData };
  }
  
  // Check for customer segments query
  if (query.includes('GetCustomerSegments')) {
    console.log('Returning mock customer segments data');
    
    // Apply filter if present
    if (variables && variables.filter) {
      const filter = variables.filter;
      let filteredSegments = [...customerSegmentsMockData];
      
      // Search by name or description
      if (filter.searchTerm) {
        const searchLower = filter.searchTerm.toLowerCase();
        filteredSegments = filteredSegments.filter(segment => 
          segment.name.toLowerCase().includes(searchLower) || 
          segment.description.toLowerCase().includes(searchLower)
        );
      }
      
      // Filter by criteria type
      if (filter.type) {
        filteredSegments = filteredSegments.filter(segment => 
          segment.criteria.some(c => c.type === filter.type)
        );
      }
      
      return { customerSegments: filteredSegments };
    }
    
    return { customerSegments: customerSegmentsMockData };
  }
  
  // Check for customers query
  if (query.includes('GetCustomers')) {
    console.log('Returning mock customers data');
    
    // Apply pagination, search, and filter
    let filteredCustomers = [...customersMockData.items];
    
    // Apply search if present
    if (variables && variables.search) {
      const searchLower = variables.search.toLowerCase();
      filteredCustomers = filteredCustomers.filter(customer => 
        customer.firstName.toLowerCase().includes(searchLower) ||
        customer.lastName.toLowerCase().includes(searchLower) ||
        customer.email.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply filter if present
    if (variables && variables.filter) {
      const filter = variables.filter;
      
      if (filter.loyaltyTier) {
        filteredCustomers = filteredCustomers.filter(customer => 
          customer.loyaltyTier === filter.loyaltyTier
        );
      }
    }
    
    // Get total count before pagination
    const totalCount = filteredCustomers.length;
    
    // Apply pagination if present
    let hasNextPage = false;
    let hasPreviousPage = false;
    
    if (variables && variables.pagination) {
      const { page = 1, limit = 10 } = variables.pagination;
      const startIndex = (page - 1) * limit;
      const endIndex = page * limit;
      
      hasPreviousPage = page > 1;
      hasNextPage = endIndex < totalCount;
      
      filteredCustomers = filteredCustomers.slice(startIndex, endIndex);
    }
    
    return { 
      customers: {
        items: filteredCustomers,
        totalCount,
        pageInfo: {
          hasNextPage,
          hasPreviousPage
        }
      } 
    };
  }
  
  // Check for policies query
  if (query.includes('GetPolicies')) {
    console.log('Returning mock policies data');
    
    // Apply filter if present
    if (variables && variables.filter) {
      const filter = variables.filter;
      let filteredPolicies = [...policiesMockData];
      
      if (filter.status) {
        filteredPolicies = filteredPolicies.filter(policy => policy.status === filter.status);
      }
      
      if (filter.ruleType) {
        filteredPolicies = filteredPolicies.filter(policy => 
          policy.rules.some(rule => rule.type === filter.ruleType)
        );
      }
      
      return { policies: filteredPolicies };
    }
    
    return { policies: policiesMockData };
  }
  
  // Handle CREATE_CUSTOMER mutation
  if (query.includes('CreateCustomer')) {
    console.log('Handling CreateCustomer mutation');
    
    const input = variables.input;
    const newCustomer = {
      id: `cust-${Date.now()}`,
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email,
      loyaltyTier: 'STANDARD',
      totalOrders: 0,
      lifetimeValue: 0,
      lastOrderDate: null,
      reCommerceActivity: {
        totalReturns: 0,
        totalRepairs: 0,
        totalResales: 0
      }
    };
    
    // Add customer to mock data
    customersMockData.items.unshift(newCustomer);
    customersMockData.totalCount += 1;
    
    return {
      createCustomer: {
        success: true,
        customer: newCustomer,
        error: null
      }
    };
  }
  
  // Default mock response
  console.warn('No mock data found for query:', query);
  return {};
}

export async function fetchGraphQL(query, variables = {}) {
  console.log('=== GraphQL Request Start ===');
  console.log('Query:', query);
  console.log('Variables:', JSON.stringify(variables, null, 2));

  // For the demo app, we'll always use mock data
  // This prevents network errors and allows working without a backend
  try {
    // Small delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 300));
    
    console.log('Using mock GraphQL data');
    return handleMockResponse(query, variables);
  } catch (error) {
    console.error('=== GraphQL Mock Error ===');
    console.error('Error handling mock data:', error);
    throw error;
  }
}